import { useStatusHandlers } from './handlers/useStatusHandlers';
import { usePositionHandlers } from './handlers/usePositionHandlers';
import { useNodeCreation } from './handlers/useNodeCreation';
import { usePasteHandlers } from './handlers/usePasteHandlers';

/**
 * useSprintHandlers Hook
 * 
 * Orchestrates all sprint-related handlers by composing specialized hooks.
 * Each handler category is extracted into its own hook for maintainability.
 * 
 * Categories:
 * - Status handlers: Add, delete, rename, WIP limit
 * - Position handlers: Node dragging, batch moves, sprint rename
 * - Node creation: Add task/decision at viewport
 * - Paste handlers: Paste and duplicate with nested sprint support
 * 
 * @param {Object} options
 * @param {Function} options.getVisibleTasks - Optional callback to get visible/filtered tasks
 *                                             Used for positioning new nodes relative to visible tasks only
 */
/**
 * @param {Object} options
 * @param {Function} options.onCustomFieldConflict - Optional async callback for custom field conflict resolution.
 *   Called when a pasted field has the same name but different type as an existing field.
 *   Signature: ({ clipboardField, existingField, existingFieldNames }) => Promise<string|null>
 *   Returns new name to use, or null to skip the field.
 */
export const useSprintHandlers = ({
  tasks,
  localSprint,
  setLocalSprint,
  nestedSprints,
  flowchartRef,
  onAddSprint,
  onUpdateSprint,
  onTrashSprint,
  onRenameSprint,
  recordAction,
  startBatch,
  endBatch,
  isMultiCampaignMode = false,
  getVisibleTasks,
  onCustomFieldConflict,
  ensureFlowView,
}) => {
  // ═══════════════════════════════════════════════════════════════
  // COMPOSED HANDLERS
  // ═══════════════════════════════════════════════════════════════

  const statusHandlers = useStatusHandlers({ setLocalSprint });

  const positionHandlers = usePositionHandlers({
    localSprint,
    setLocalSprint,
    onRenameSprint,
    recordAction,
    startBatch,
    endBatch,
    isMultiCampaignMode,
  });

  const nodeCreation = useNodeCreation({
    localSprint,
    setLocalSprint,
    flowchartRef,
    recordAction,
    getVisibleTasks,
    ensureFlowView,
  });

  const pasteHandlers = usePasteHandlers({
    tasks,
    localSprint,
    setLocalSprint,
    nestedSprints,
    flowchartRef,
    onAddSprint,
    onUpdateSprint,
    onTrashSprint,
    recordAction,
    onCustomFieldConflict,
  });

  // ═══════════════════════════════════════════════════════════════
  // RETURN FLATTENED API
  // ═══════════════════════════════════════════════════════════════

  return {
    // Status handlers
    handleAddStatus: statusHandlers.handleAddStatus,
    handleDeleteStatus: statusHandlers.handleDeleteStatus,
    handleRenameStatus: statusHandlers.handleRenameStatus,
    handleSetWipLimit: statusHandlers.handleSetWipLimit,
    
    // Position/sprint handlers
    handleRenameSprint: positionHandlers.handleRenameSprint,
    handlePositionChange: positionHandlers.handlePositionChange,
    handlePositionChangeBatch: positionHandlers.handlePositionChangeBatch,
    
    // Node creation
    handleAddNodeAtViewport: nodeCreation.handleAddNodeAtViewport,
    handleAddTaskAtViewport: nodeCreation.handleAddTaskAtViewport,
    handleAddDecisionAtViewport: nodeCreation.handleAddDecisionAtViewport,
    handleAddWaitAtViewport: nodeCreation.handleAddWaitAtViewport,
    handleAddMilestoneOnHost: nodeCreation.handleAddMilestoneOnHost,
    handleAddNoteOnHost: nodeCreation.handleAddNoteOnHost,
    handleInsertMilestoneOnEdge: nodeCreation.handleInsertMilestoneOnEdge,
    handleCreateParallelMission: nodeCreation.handleCreateParallelMission,
    handleInsertMissionBefore: nodeCreation.handleInsertMissionBefore,
    
    // Clipboard
    handlePasteTasks: pasteHandlers.handlePasteTasks,
    handleDuplicateTaskWithNested: pasteHandlers.handleDuplicateTaskWithNested,
    handlePasteWithCampaign: pasteHandlers.handlePasteWithCampaign,
  };
};

export default useSprintHandlers;
